import { createSelector } from '@reduxjs/toolkit';
import { message } from 'antd';
import _ from 'lodash';
import { all, put, select, takeLatest } from 'redux-saga/effects';
import { Automation } from 'api/automations';
import { RootState } from 'data';
import { StoreActionProps } from 'data/core';
import { AutomationLiteForTemplate } from 'data/templates/api';
import { logger } from 'services';
import {
  TaskIdPayload,
  deleteTaskInProjectTemplate,
  updateAutomationByIdAction,
} from '../../actions';
import { selectTemplateTask } from '../../selectors';
import {
  deleteProjectTemplateSubTask,
  deleteProjectTemplateTask,
  setProjectTemplateActiveRecord,
} from '../../slices';
import { ProjectTemplateActiveRecord, ProjectTemplateTask } from '../../types';

const selectActiveRecord = createSelector(
  (state: RootState) => state.templates.project.activeRecord,
  (
    activeRecord: ProjectTemplateActiveRecord | undefined,
  ): ProjectTemplateActiveRecord | undefined => activeRecord,
);

const selectAutomationsInActiveTemplate = (
  state: RootState,
): (Automation | AutomationLiteForTemplate)[] => {
  const { activeId } = state.templates.project;

  return state.templates.project.data[activeId]?.template?.automations ?? [];
};

function* getAutomationsForTask(
  taskId: number,
): Generator<any, (Automation | AutomationLiteForTemplate)[], any> {
  const automations: (Automation | AutomationLiteForTemplate)[] = yield select(
    selectAutomationsInActiveTemplate,
  );

  return automations.filter(
    (e) => e.tags?.taskTemplateInstanceReferenceId === taskId && !e.deleted,
  );
}

function* markAutomationsAsDeletedForTask(taskId: number) {
  const automationsForTask: (Automation | AutomationLiteForTemplate)[] =
    yield getAutomationsForTask(taskId);

  yield all(
    automationsForTask.map((automation) =>
      put(
        updateAutomationByIdAction({
          automation: _.cloneDeep({
            ...automation,
            deleted: true,
          } as Automation),
          automationId: automation.automationId,
        }),
      ),
    ),
  );
}

export function* isSameTaskOrChildTask(task, activeTaskId) {
  if (task.taskId === activeTaskId) {
    return true;
  }

  const { subTasks } = task;

  if (subTasks) {
    for (let i = 0; i < subTasks.length; i += 1) {
      if (activeTaskId === subTasks[i]) {
        return true;
      }
      const subTask = yield select(selectTemplateTask(subTasks[i]));

      if (yield isSameTaskOrChildTask(subTask, activeTaskId)) {
        return true;
      }
    }
  }

  return false;
}

function* deleteTaskInTemplateSaga(action: StoreActionProps<TaskIdPayload>) {
  try {
    const {
      payload: { taskId },
    } = action;

    const task: ProjectTemplateTask = yield select(selectTemplateTask(taskId));
    const activeRecord = yield select(selectActiveRecord);

    const shouldChangeActiveRecord =
      activeRecord && activeRecord.type === 'TASK'
        ? yield isSameTaskOrChildTask(task, activeRecord.id)
        : false;

    // Mark any automations associated with this task as deleted
    // This prevents save failures when the task is removed but automations still reference it
    yield markAutomationsAsDeletedForTask(taskId);

    if (task.projectPhase) {
      yield put(deleteProjectTemplateTask({ taskId }));

      if (shouldChangeActiveRecord) {
        yield put(
          setProjectTemplateActiveRecord({
            id: task.projectPhase.projectPhaseId,
            type: 'PROJECT_PHASE',
          }),
        );
      }
    } else {
      yield put(deleteProjectTemplateSubTask({ taskId }));

      if (shouldChangeActiveRecord) {
        yield put(
          setProjectTemplateActiveRecord({
            id: task.parentTask.taskId,
            type: 'TASK',
          }),
        );
      }
    }
  } catch (error) {
    logger.error('deleteTaskInTemplateSaga failed ', { error });

    message.error('There was an error deleting the task');
  }
}

export function* watchDeleteTaskInProjectTemplateSaga(): Generator {
  yield takeLatest(deleteTaskInProjectTemplate.type, deleteTaskInTemplateSaga);
}
